import React from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import CustomInput from "../TextField/TextFeild";
import { closeScheduleSendNotificationModel, scheduleEventNotification } from "../../store/slices/eventSlice";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import { HUMAN_TIMEZONES } from "../../constants";

const EXCLUDED_NOTIFICATION_GROUPS = new Set(["radiomax", "radiofx", "radiiofx"]);

const getSelectableStationGroups = (stationGroupString) => {
    const groups = (stationGroupString || "")
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean)
        .filter((g) => !EXCLUDED_NOTIFICATION_GROUPS.has(g.toLowerCase()));
    return [...new Set(groups)];
};

const validationSchema = Yup.object({
    title: Yup.string().max(100, 'Subject must be less then 100 characters').required("Subject is required"),
    body: Yup.string().max(255, 'Message must be less then 255 characters').required("Message is required"),
    tz: Yup.string().required("Timezone is required"),
    date: Yup.string().required("Date is required"),
    time: Yup.string()
        .required("Time is required")
        .matches(
            /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/,
            "Time must be in the format hh:mm AM/PM"
        )
        .test('future-time', 'Notification must be scheduled at least 5 minutes in advance', function (value) {
            const { date, tz } = this.parent;
            if (!date || !value || !tz) return true;

            try {
                const timeZone = HUMAN_TIMEZONES[tz] || tz;
                const scheduledTime = moment.tz(`${date} ${value}`, 'YYYY-MM-DD hh:mm A', timeZone);
                const now = moment.tz(timeZone);
                const minutesUntilScheduled = scheduledTime.diff(now, 'minutes', true);

                if (minutesUntilScheduled < 5) {
                    return this.createError({
                        message: `Notification must be scheduled at least 5 minutes in advance. Currently ${minutesUntilScheduled.toFixed(1)} minutes away.`
                    });
                }

                return true;
            } catch (error) {
                return this.createError({
                    message: 'Invalid date/time combination'
                });
            }
        }),
});

const ScheduleNotificationModel = ({ show }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const selectableGroups = getSelectableStationGroups(user?.attributes?.stationGroup);
    const showGroupSelector = selectableGroups.length > 1;
    const hideModel = () => dispatch(closeScheduleSendNotificationModel());

    const formik = useFormik({
        initialValues: {
            title: "",
            body: "",
            deeplink: "",
            tz: "",
            date: "",
            time: "",
            stationGroups: selectableGroups,
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            if (showGroupSelector && (!values.stationGroups || values.stationGroups.length === 0)) {
                formik.setFieldError("stationGroups", "Select at least one StationGroup/App");
                return;
            }
            const payload = {
                ...values,
            };

            dispatch(scheduleEventNotification({ message: "Notification Scheduled successfully.", palyload: payload }))
                .unwrap()
                .then(() => {
                    hideModel();
                    formik.resetForm();
                });
        },
    });

    const toggleStationGroup = (group) => {
        const current = formik.values.stationGroups || [];
        const next = current.includes(group)
            ? current.filter((g) => g !== group)
            : [...current, group];
        formik.setFieldValue("stationGroups", next);
    };


    return (
        <>
            {show && <div className="drawer-overlay" onClick={hideModel}></div>}

            <div className={`drawer ${show ? "open" : ""} overflow-y-auto`}>
                <form className="bg-white h-100 d-flex flex-column" onSubmit={formik.handleSubmit}>

                    <p className="notification_heading m-0 text-center bg-dark text-white">SCHEDULE NOTIFICATION</p>

                    <div className="form-card p-3 flex-grow-1">
                        <CustomInput
                            label="Subject"
                            id="subject"
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.title && formik.errors.title}
                            required
                        />

                        <CustomInput
                            label="Message"
                            id="message"
                            name="body"
                            value={formik.values.body}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.body && formik.errors.body}
                            required
                        />

                        <CustomInput
                            label="Deep Link (optional)"
                            id="deepLink"
                            name="deeplink"
                            value={formik.values.deeplink}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.deeplink && formik.errors.deeplink}
                        />
                        <TimezoneSelect
                            label="Timezone"
                            id="timezone"
                            name="tz"
                            value={formik.values.tz}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tz && formik.errors.tz}
                        />
                        <CustomInput
                            type="date"
                            label="Date"
                            name="date"
                            value={formik.values.date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.date && formik.errors.date}
                        />
                        <CustomInput
                            type="time"
                            label="Time"
                            name="time"
                            value={
                                formik.values.time
                                    ? moment(formik.values.time, "hh:mm A").format("HH:mm")
                                    : ""
                            }
                            onChange={(e) => {
                                const value24 = e.target.value; // 14:53
                                const value12 = moment(value24, "HH:mm").format("hh:mm A");

                                formik.setFieldValue("time", value12);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.time && formik.errors.time}
                        />
                        {showGroupSelector && (
                            <div className="my-3">
                                <label className="mb-2 fw-semibold">StationGroups / Apps</label>
                                <div className="d-flex flex-column gap-1">
                                    {selectableGroups.map((group) => (
                                        <label key={group} className="d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={(formik.values.stationGroups || []).includes(group)}
                                                onChange={() => toggleStationGroup(group)}
                                            />
                                            <span>{group}</span>
                                        </label>
                                    ))}
                                </div>
                                {formik.errors.stationGroups ? (
                                    <div className="error-text">{formik.errors.stationGroups}</div>
                                ) : null}
                            </div>
                        )}

                    </div>

                    <div className="p-3 text-left border-top">

                        <button type="submit" className="btn btn-lg btn-secondary" disabled={formik.isSubmitting}>
                            {formik.isSubmitting ? "Scheduling..." : "Schedule Notification"}
                        </button>
                        <button type="button" className="btn btn-lg btn-outline-danger mx-3" onClick={hideModel}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default ScheduleNotificationModel;
